/**
 * Isi Panduan Pengguna e-Sign UI.
 *
 * Satu sumber untuk dua keluaran:
 *   - halaman web /help  (src/views/help/Panduan.vue)
 *   - file PDF per peran (scripts/build-help-pdf.js -> public/help/User_Manual_Esign_<Peran>.pdf)
 *
 * ES module; dimuat Node lewat import() dinamis di scripts/build-help-pdf.js.
 *
 * Jenis blok:
 *   { p: 'teks' }                              paragraf
 *   { list: ['..', '..'] }                     daftar berbutir
 *   { steps: ['..', '..'] }                    langkah bernomor
 *   { img: 'nama.png', caption: '..' }         gambar dari /help/img/
 *   { callout: 'info'|'penting'|'tips'|'catatan', text: '..' }
 *   { table: { head: ['..'], rows: [['..']] } }
 *   { h3: 'Subjudul' }                         subbagian (masuk daftar isi)
 * Teks mendukung **tebal**, *miring*, dan `kode`.
 */

const DOC = {
  noDokumen: 'F-DTD-10-07',
  revisi: '00',
  klasifikasi: 'Rahasia',
  tanggalTerbit: '5 November 2026',
  namaProyek: 'PENGEMBANGAN SISTEM E-SIGN UNIVERSITAS INDONESIA',
  namaSistem: 'E-SIGN UNIVERSITAS INDONESIA',
  judulSingkat: 'E-SIGN UI',
  programId: 'ESIGN',
  versi: '1.0',
  url: 'https://esign.ui.ac.id',
  diperbarui: '05/11/2026',
}

// ---------------------------------------------------------------------------
// Bagian yang dipakai bersama oleh semua peran
// ---------------------------------------------------------------------------

const masuk = (peranLabel) => ({
  id: 'masuk',
  title: 'Masuk & Beranda',
  blocks: [
    { p: `Buka **${DOC.url}** melalui browser (Google Chrome, Mozilla Firefox, Microsoft Edge, atau Safari). Halaman masuk menyediakan dua cara masuk.` },
    { img: 'umum-login.png', caption: 'Halaman masuk e-Sign' },
    { list: [
      '**Login SSO**: untuk civitas UI. Gunakan akun SSO UI (username dan password yang sama dengan layanan UI lainnya).',
      '**Login Internal**: untuk akun internal yang dibuat oleh Administrator. Isi **Username**, **Password**, centang **reCAPTCHA**, lalu klik **Login**. Bila lupa password, klik **Forgot Password?**.',
      '**Verifikasi File**: memeriksa keaslian dokumen PDF bertanda tangan tanpa perlu masuk (lihat bagian Verifikasi Dokumen).',
    ] },
    { img: 'umum-login-internal.png', caption: 'Form Login Internal' },
    { p: `Setelah berhasil masuk, sistem membuka halaman **Profil Pengguna** yang berisi sapaan, peran aktif, kartu pintasan **Mulai dari sini**, dan data profil Anda. Menu di bagian atas menyesuaikan peran aktif (*${peranLabel}*).` },
    { callout: 'info', text: 'Sesi otomatis berakhir bila tidak ada aktivitas selama **1 jam**. Masuk kembali untuk melanjutkan.' },
  ],
})

const gantiPeran = {
  id: 'ganti-peran',
  title: 'Ganti Peran & Keluar',
  blocks: [
    { p: 'Satu akun dapat memiliki lebih dari satu peran (misalnya Pengaju sekaligus Penandatangan). Menu yang tampil hanya milik **peran aktif**.' },
    { steps: [
      'Klik **avatar** (lingkaran inisial) di kanan atas.',
      'Pada kolom **Peran aktif**, pilih peran yang diinginkan.',
      'Halaman dimuat ulang dan menu berganti sesuai peran yang dipilih.',
    ] },
    { img: 'umum-ganti-peran.png', caption: 'Mengganti peran aktif' },
    { p: 'Untuk keluar dari aplikasi, klik avatar lalu klik **Logout**.' },
  ],
}

const notifikasi = {
  id: 'notifikasi',
  title: 'Notifikasi',
  blocks: [
    { p: 'Ikon **lonceng** di kanan atas menampilkan jumlah notifikasi yang belum dibaca, misalnya hasil proses tanda tangan (berhasil, sedang diproses, atau gagal). Klik lonceng untuk melihat notifikasi terbaru, atau **Lihat semua** untuk membuka halaman Notifikasi.' },
    { p: 'Di halaman **Notifikasi**, saring berdasarkan **Status Notifikasi**, lalu klik **Mark All Read** untuk menandai semua notifikasi sudah dibaca.' },
    { img: 'admin-notifikasi.png', caption: 'Halaman Notifikasi' },
  ],
}

const akun = {
  id: 'akun',
  title: 'Profil, Tanda Tangan & Password',
  blocks: [
    { p: 'Halaman **Profil Pengguna** (menu **Akun › Profil Pengguna**) menampilkan username, nama, NIK, jabatan, email, organisasi, masa berlaku password, **Status BSrE**, file tanda tangan & paraf, serta daftar peran.' },
    { img: 'admin-profil.png', caption: 'Halaman Profil Pengguna' },
    { h3: 'Mengunggah file tanda tangan & paraf' },
    { steps: [
      'Klik **Ubah profil**.',
      'Lengkapi **Nama Lengkap**, **Email**, dan data lain bila perlu.',
      'Unggah gambar pada **File Tanda Tangan** dan **File Paraf** (disarankan PNG berlatar transparan).',
      'Klik **Simpan**.',
    ] },
    { callout: 'penting', text: 'File tanda tangan/paraf wajib ada bila jenis dokumen meminta **visual** tanda tangan/paraf. Tanpa file ini, pengaju tidak dapat menambahkan Anda sebagai penandatangan ("Penandatangan belum memiliki file tanda tangan").' },
    { h3: 'Ganti password (akun internal)' },
    { steps: [
      'Buka **Akun › Ganti Password**.',
      'Isi **Password lama**, **Password baru**, dan **Konfirmasi password baru**.',
      'Password baru minimal **10 karakter** dan memuat huruf besar, huruf kecil, angka, dan simbol (! @ # $ % ^ & * + = . _ -).',
      'Klik **Simpan password**. Anda akan keluar otomatis dan perlu masuk kembali.',
    ] },
    { img: 'admin-ganti-password.png', caption: 'Halaman Ganti Password' },
    { callout: 'catatan', text: 'Pengguna yang masuk melalui **SSO** mengganti password melalui layanan SSO UI, bukan melalui e-Sign.' },
  ],
}

const verifikasi = {
  id: 'verifikasi',
  title: 'Verifikasi Dokumen',
  blocks: [
    { p: 'Menu **File Verification** memeriksa keaslian tanda tangan elektronik pada dokumen PDF yang diterbitkan melalui e-Sign UI. Halaman ini juga dapat dibuka tanpa masuk melalui tombol **Verifikasi File** di halaman masuk, atau dengan memindai **QR code** pada dokumen.' },
    { steps: [
      'Tarik file PDF ke area **Tarik file PDF ke sini**, atau klik **pilih dari perangkat**. Hanya file PDF yang diterima.',
      'Klik **Periksa tanda tangan**.',
      'Hasil menampilkan **Tanda tangan valid** atau **Tanda tangan tidak valid**, beserta nama file, nomor surat, tanggal pengajuan, jumlah tanda tangan, dan daftar penandatangan (penerbit sertifikat, waktu tanda tangan, status sertifikat, dan apakah isi dokumen berubah).',
      'Klik **Periksa file lain** untuk memeriksa dokumen berikutnya.',
    ] },
    { img: 'admin-verify.png', caption: 'Halaman Verifikasi Dokumen' },
    { img: 'umum-verify-hasil.png', caption: 'Contoh hasil verifikasi: tanda tangan valid' },
    { callout: 'info', text: 'File **tidak disimpan** oleh sistem. Pemeriksaan dilakukan oleh BSrE (Balai Sertifikasi Elektronik).' },
  ],
}

const statusDokumen = {
  table: {
    head: ['Status', 'Arti'],
    rows: [
      ['Diajukan', 'Dokumen baru diajukan dan belum ada yang memaraf/menandatangani.'],
      ['Dalam Proses', 'Sebagian pemaraf/penandatangan sudah memproses; menunggu yang lain.'],
      ['Dalam Review', 'Ada penandatangan yang **menolak**; pengaju perlu memutuskan (Konfirm Tolak atau Sanggah Tolak).'],
      ['Selesai', 'Semua pemaraf dan penandatangan telah memproses. Dokumen dapat diunduh.'],
      ['Dibatalkan', 'Pengajuan dibatalkan oleh pengaju, atau satu-satunya penandatangan menolak dan penolakan dikonfirmasi.'],
    ],
  },
}

const alurPersetujuan = {
  table: {
    head: ['Alur persetujuan', 'Penandatangan', 'Urutan'],
    rows: [
      ['Langsung', 'Tepat **1** penandatangan', '-'],
      ['Berjenjang', 'Lebih dari 1', '**Berurutan**: penandatangan berikutnya baru dapat menandatangani setelah yang sebelumnya selesai.'],
      ['Grup', 'Lebih dari 1', '**Bebas**: semua penandatangan dapat menandatangani kapan saja.'],
    ],
  },
}

// ---------------------------------------------------------------------------
// ADMINISTRATOR
// ---------------------------------------------------------------------------

const admin = {
  kode: 'A01',
  label: 'Administrator',
  slug: 'Administrator',
  intro: [
    { p: 'Administrator mengelola konfigurasi e-Sign UI tingkat universitas:' },
    { list: [
      'memelihara **master data**: organisasi, jenis dokumen, jenis dokumen organisasi (template, alur, pemaraf & penandatangan), dan pengguna;',
      'mengatur **konfigurasi sistem**, hak akses menu per peran, dan integrasi **aplikasi eksternal**;',
      'memantau seluruh **dokumen**, **batch**, dan **log aktivitas**;',
      'menonaktifkan dokumen yang tidak lagi berlaku.',
    ] },
    { callout: 'info', text: 'Alur dokumen: **Admin menyiapkan Jenis Dokumen Organisasi → Pengaju mengajukan dokumen → Pemaraf memaraf → Penandatangan menandatangani (BSrE) → Selesai → Dokumen dapat diunduh & diverifikasi.**' },
  ],
  sections: [
    masuk('Administrator'),
    {
      id: 'menu-admin',
      title: 'Menu Administrator',
      blocks: [
        { p: 'Menu utama Administrator terdiri dari **Akun**, **Master Data**, **Informasi**, **Dokumen**, **Batch**, **Pengajuan Tandatangan Dokumen**, dan **File Verification**.' },
        { img: 'admin-menu-masterdata.png', caption: 'Submenu Master Data' },
        { table: {
          head: ['Menu', 'Submenu', 'Kegunaan'],
          rows: [
            ['Master Data', 'Organisasi', 'Daftar PAU/Fakultas/Sekolah beserta file stamp'],
            ['', 'Jenis Dokumen', 'Nama jenis dokumen dan alur persetujuannya'],
            ['', 'Pengguna', 'Akun pengguna, file tanda tangan/paraf, dan peran'],
            ['', 'Jenis Dokumen Organisasi', 'Template, tingkat kerahasiaan, pemaraf, penandatangan, dan posisi tanda tangan per organisasi'],
            ['Informasi', 'Log Aktivitas', 'Riwayat aktivitas seluruh pengguna'],
            ['', 'Pengaturan', 'Konfigurasi sistem (token, login, BSrE, dll.)'],
            ['', 'Menu', 'Hak akses menu tiap peran (User Access Matrix)'],
            ['', 'Pengaturan Eksternal', 'Integrasi aplikasi lain yang mengirim dokumen ke e-Sign'],
          ],
        } },
      ],
    },
    {
      id: 'organisasi',
      title: 'Master Data: Organisasi',
      blocks: [
        { p: 'Menu **Master Data › Organisasi** menampilkan daftar PAU/Fakultas/Sekolah. Gunakan filter **Parent** dan **Level**, atau kolom pencarian kode/nama organisasi.' },
        { img: 'admin-organisasi.png', caption: 'Daftar Organisasi' },
        { h3: 'Menambah organisasi' },
        { steps: [
          'Klik **Tambah**.',
          'Isi **Kode Organisasi** dan **Nama Organisasi** (wajib).',
          'Pilih **Parent** (organisasi induk) dan **Level** bila ada.',
          'Unggah **File Stamp** bila organisasi memiliki stempel elektronik.',
          'Klik **Simpan**.',
        ] },
        { img: 'admin-tambah-org.png', caption: 'Form Tambah Organisasi' },
        { p: 'Klik **Detail** untuk melihat data organisasi dan file stamp (**Lihat Gambar**), **Ubah** untuk memperbarui, atau **Hapus** untuk menghapus.' },
        { img: 'admin-detail-org.png', caption: 'Detail Organisasi' },
      ],
    },
    {
      id: 'jenis-dokumen',
      title: 'Master Data: Jenis Dokumen',
      blocks: [
        { p: 'Menu **Master Data › Jenis Dokumen** berisi daftar jenis dokumen beserta **alur persetujuan** dan statusnya.' },
        { img: 'admin-jenis-dokumen.png', caption: 'Daftar Jenis Dokumen' },
        { steps: [
          'Klik **Tambah**.',
          'Isi **Nama Dokumen**.',
          'Pilih **Alur** persetujuan: **Langsung**, **Berjenjang**, atau **Grup**.',
          'Klik **Simpan**.',
        ] },
        alurPersetujuan,
        { img: 'admin-tambah-jd.png', caption: 'Form Tambah Jenis Dokumen' },
        { p: 'Melalui **Ubah**, Administrator juga dapat mengganti alur dan **Status** (Aktif/Tidak Aktif). Jenis dokumen yang tidak aktif tidak muncul saat pengajuan.' },
      ],
    },
    {
      id: 'jdo',
      title: 'Master Data: Jenis Dokumen Organisasi',
      blocks: [
        { p: 'Jenis Dokumen Organisasi menghubungkan **jenis dokumen** dengan **organisasi**, lalu menetapkan template, tingkat kerahasiaan, pemaraf, penandatangan, serta **posisi** paraf/tanda tangan pada dokumen. Pengaju hanya dapat mengajukan jenis dokumen yang sudah dikonfigurasi di sini.' },
        { img: 'admin-jdo.png', caption: 'Daftar Jenis Dokumen Organisasi' },
        { h3: 'Menambah jenis dokumen organisasi' },
        { steps: [
          'Klik **Tambah**.',
          'Pilih **Organisasi**, **Jenis Dokumen**, dan **Tingkat Kerahasiaan** (Biasa, Rahasia, Sangat Rahasia). **Workflow Persetujuan** terisi otomatis mengikuti jenis dokumen.',
          'Unggah **File Template** (PDF) sebagai acuan posisi tanda tangan.',
          'Pilih **Jenis Tanda Tangan** (Stamp dan/atau QR).',
          'Pada **Daftar Pemaraf**, pilih pemaraf lalu klik **Tambah**. Centang **Perlu Visual Paraf** bila gambar paraf harus tampil.',
          'Pada **Daftar Penandatangan**, pilih penandatangan lalu klik **Tambah**. Centang **Perlu Visual Tandatangan** bila gambar tanda tangan harus tampil. Untuk alur **Berjenjang**, urutan di tabel menjadi urutan tanda tangan.',
          'Klik **Preview**, lalu seret setiap item (stamp, tanda tangan, paraf, QR) ke posisi yang diinginkan pada dokumen. Gunakan kotak nomor halaman untuk berpindah halaman.',
          'Centang **Saya telah mengkonfirmasi posisi tanda tangan dan kesesuaian dokumen di atas**, lalu klik **Simpan**.',
        ] },
        { img: 'admin-tambah-jdo.png', caption: 'Form Tambah Jenis Dokumen Organisasi' },
        { callout: 'penting', text: 'Pemaraf/penandatangan dengan visual paraf/tanda tangan harus sudah memiliki **file paraf/tanda tangan** pada data pengguna. Posisi yang disimpan di sini dipakai otomatis setiap kali pengaju mengajukan dokumen jenis ini.' },
        { p: 'Klik **Detail** untuk melihat konfigurasi, atau **Ubah** untuk memperbarui template, pemaraf, penandatangan, dan posisi.' },
        { img: 'admin-detail-jdo.png', caption: 'Detail Jenis Dokumen Organisasi' },
      ],
    },
    {
      id: 'pengguna',
      title: 'Master Data: Pengguna',
      blocks: [
        { p: 'Menu **Master Data › Pengguna** menampilkan seluruh akun beserta organisasi, email, dan status. Saring berdasarkan **Username/Nama** atau **Organisasi**.' },
        { img: 'admin-pengguna.png', caption: 'Daftar Pengguna' },
        { h3: 'Menambah pengguna' },
        { steps: [
          'Klik **Tambah**.',
          'Pilih **Sumber**: **Internal** (login dengan username & password e-Sign) atau **Eksternal**/SSO.',
          'Isi **Username**, **Nama Lengkap**, **NIK**, **Jabatan**, **Email**, dan **Organisasi**.',
          'Unggah **File Tanda Tangan** dan **File Paraf** bila pengguna akan menjadi penandatangan/pemaraf.',
          'Pilih **Peran Pengguna** (Administrator, Pengaju, Penandatangan, dll.).',
          'Klik **Simpan**.',
        ] },
        { img: 'admin-tambah-pengguna.png', caption: 'Form Tambah Pengguna' },
        { callout: 'penting', text: '**NIK** harus sesuai dengan NIK yang terdaftar di BSrE. NIK dipakai untuk mencocokkan sertifikat elektronik saat penandatanganan; **Status BSrE** pada detail pengguna menunjukkan hasil pengecekannya.' },
        { p: 'Pada daftar pengguna tersedia aksi **Detail** (termasuk menambah/menghapus peran), **Ubah**, dan **Reset Password** untuk akun internal.' },
        { img: 'admin-detail-pengguna.png', caption: 'Detail Pengguna' },
      ],
    },
    {
      id: 'log',
      title: 'Informasi: Log Aktivitas',
      blocks: [
        { p: 'Menu **Informasi › Log Aktivitas** mencatat aktivitas pengguna: login, pengajuan, tanda tangan, perubahan data, dan proses otomatis sistem. Saring berdasarkan **Username/Nama**, **Tanggal Awal**, dan **Tanggal Akhir**, lalu klik **Cari**.' },
        { img: 'admin-log.png', caption: 'Log Aktivitas Pengguna' },
        { p: 'Klik **Detail** untuk melihat waktu, jenis aktivitas, peran, tabel yang berubah, IP address, user agent, serta data **Sebelum** dan **Sesudah** perubahan.' },
        { img: 'admin-detail-log.png', caption: 'Detail Log Aktivitas' },
      ],
    },
    {
      id: 'pengaturan',
      title: 'Informasi: Pengaturan',
      blocks: [
        { p: 'Menu **Informasi › Pengaturan** berisi konfigurasi sistem. Klik **Ubah** pada baris yang diinginkan, ubah **Nilai Pengaturan**, lalu klik **Simpan**.' },
        { img: 'admin-pengaturan.png', caption: 'Daftar Konfigurasi Sistem' },
        { table: {
          head: ['Pengaturan', 'Fungsi'],
          rows: [
            ['TOKEN_HOUR_LIFESPAN', 'Masa berlaku token akses (jam)'],
            ['USER_LOGIN_INTERVAL_TIME_MINUTES', 'Jarak waktu percobaan login (menit)'],
            ['USER_BLOCK_TIME_MINUTES', 'Lama blokir setelah gagal login berulang (menit)'],
            ['ADMIN_/USER_PASSWORD_DEACTIVATION_TIME_DAY', 'Masa berlaku password admin/pengguna (hari)'],
            ['USER_ACCOUNT_DEACTIVATION_TIME_DAY', 'Akun dinonaktifkan bila tidak dipakai selama N hari'],
            ['API_BSRE_URL / USERNAME / PASSWORD', 'Koneksi ke layanan tanda tangan BSrE'],
            ['BSRE_DISCLAIMER / BSRE_DISCLAIMER_FONT_SIZE', 'Teks dan ukuran huruf disclaimer BSrE pada footer dokumen'],
            ['API_GATEWAY / ESIGN_CLIENT_ID', 'Koneksi Keycloak/SSO dan API Gateway UI'],
          ],
        } },
        { callout: 'penting', text: 'Perubahan pengaturan langsung berlaku untuk seluruh pengguna. Ubah pengaturan koneksi (BSrE, API Gateway, Client ID) hanya atas koordinasi dengan DTD.' },
      ],
    },
    {
      id: 'menu-uam',
      title: 'Informasi: Menu & Hak Akses',
      blocks: [
        { p: 'Menu **Informasi › Menu** menampilkan **User Access Matrix**, yaitu daftar menu yang dapat diakses tiap peran.' },
        { img: 'admin-menu.png', caption: 'Daftar Menu & User Access Matrix' },
        { steps: [
          'Klik **Detail** pada peran yang ingin diatur.',
          'Klik menu di kolom **Menu tersedia** lalu **Tambahkan** untuk memberi akses, atau pilih menu di kolom **Bisa diakses** lalu **Keluarkan** untuk mencabut akses.',
          'Klik **Simpan perubahan**.',
        ] },
        { img: 'admin-detail-uam.png', caption: 'Pengaturan akses menu per peran' },
        { callout: 'catatan', text: 'Pengguna perlu keluar dan masuk kembali agar perubahan hak akses berlaku.' },
      ],
    },
    {
      id: 'pengaturan-eksternal',
      title: 'Informasi: Pengaturan Eksternal',
      blocks: [
        { p: 'Menu **Informasi › Pengaturan Eksternal** mengelola aplikasi lain (mis. SIKERJASAMA, SKPI, HRIS) yang mengirim dokumen ke e-Sign melalui API, dan menerima pemberitahuan (*callback*) ketika dokumen selesai ditandatangani.' },
        { img: 'admin-pengaturan-eksternal.png', caption: 'Daftar Pengaturan Eksternal' },
        { steps: [
          'Klik **Tambah**.',
          'Isi **Nama** aplikasi, **Secret Key**, dan URL **CallBack**.',
          'Isi **Daftar Whitelist IP** (pisahkan dengan koma) sebagai alamat IP yang boleh memanggil API.',
          'Isi **Auth Config**, **Header Config**, dan **Body Config** bila callback memerlukannya, serta **Keterangan**.',
          'Klik **Simpan**.',
        ] },
        { img: 'admin-tambah-pe.png', caption: 'Form Tambah Pengaturan Eksternal' },
        { callout: 'penting', text: '**Secret Key** bersifat rahasia. Jangan membagikannya selain kepada pengelola aplikasi terkait.' },
      ],
    },
    {
      id: 'pemantauan',
      title: 'Pemantauan Dokumen & Batch',
      blocks: [
        { p: 'Menu **Dokumen** menampilkan seluruh arsip dokumen. Saring berdasarkan **Username Pengaju**, **Nama Penandatangan**, **Nomor Surat**, **Tanggal Pengajuan**, **Jenis Dokumen**, dan **Status Dokumen**.' },
        { callout: 'tips', text: 'Filter **Status Dokumen** terisi **Selesai** saat halaman dibuka. Kosongkan atau ganti filter ini untuk melihat dokumen dengan status lain.' },
        { img: 'admin-dokumen.png', caption: 'Daftar Arsip Dokumen' },
        statusDokumen,
        { p: 'Centang beberapa dokumen lalu klik **Download ZIP** untuk mengunduh sekaligus (1 sampai 500 dokumen). Dokumen yang belum selesai diberi akhiran status pada nama filenya.' },
        { p: 'Menu **Batch** menampilkan kumpulan dokumen yang dikirim aplikasi eksternal (kolom **Source**). Klik **Detail** untuk melihat isi batch.' },
        { img: 'admin-batch.png', caption: 'Daftar Arsip Batch' },
        { h3: 'Menonaktifkan dokumen' },
        { p: 'Bila dokumen yang sudah ditandatangani dinyatakan tidak berlaku:' },
        { steps: [
          'Buka **Detail** dokumen.',
          'Klik tombol **⋮** (Aksi lainnya) di samping **Preview Dokumen**, lalu pilih **Nonaktifkan dokumen**.',
          'Pada dialog "Apakah anda yakin ingin menonaktifkan dokumen ini?", isi **Ketikkan Catatan Penonaktifan**.',
          'Klik **Ya**. Klik **Batal** bila tidak jadi.',
        ] },
        { img: 'admin-detail-menu.png', caption: 'Menu Nonaktifkan dokumen pada Detail Dokumen' },
        { img: 'admin-nonaktif-dialog.png', caption: 'Dialog konfirmasi penonaktifan dokumen' },
        { p: 'Dokumen tetap tersimpan, tetapi **Keaktifan** berubah menjadi **Tidak Aktif** dan catatan tampil sebagai **Keterangan Nonaktif**. Dokumen dianggap tidak valid meskipun telah ditandatangani melalui BSrE.' },
        { callout: 'catatan', text: 'Hanya peran **Administrator** yang dapat menonaktifkan dokumen.' },
      ],
    },
    {
      id: 'pengajuan-admin',
      title: 'Pengajuan Tandatangan Dokumen',
      blocks: [
        { p: 'Administrator juga dapat mengajukan dokumen melalui menu **Pengajuan Tandatangan Dokumen**. Langkahnya sama dengan peran Pengaju (lihat **Panduan Pengaju**).' },
        { img: 'admin-submit.png', caption: 'Pilihan jumlah dokumen yang diajukan' },
      ],
    },
    verifikasi,
    notifikasi,
    gantiPeran,
    akun,
  ],
}

// ---------------------------------------------------------------------------
// PENGAJU
// ---------------------------------------------------------------------------

const pengaju = {
  kode: 'A02',
  label: 'Pengaju',
  slug: 'Pengaju',
  intro: [
    { p: 'Pengaju mengajukan dokumen untuk diparaf dan ditandatangani secara elektronik:' },
    { list: [
      'mengajukan **satu dokumen** atau **banyak dokumen sekaligus** (ZIP + CSV);',
      'memantau status dan riwayat penandatanganan;',
      'menindaklanjuti **penolakan** dari penandatangan;',
      'membatalkan pengajuan dan mengunduh dokumen yang sudah selesai.',
    ] },
    { callout: 'info', text: 'Alur dokumen: **Pengaju mengajukan → Pemaraf memaraf → Penandatangan menandatangani → Selesai → Pengaju mengunduh dokumen.** Bila ada yang menolak, status menjadi **Dalam Review** dan pengaju memutuskan tindak lanjutnya.' },
  ],
  sections: [
    masuk('Pengaju'),
    {
      id: 'ajukan-satu',
      title: 'Mengajukan Satu Dokumen',
      blocks: [
        { p: 'Buka menu **Pengajuan Tandatangan Dokumen**, lalu pilih **Satu dokumen**.' },
        { img: 'pengaju-submit-pilih.png', caption: 'Memilih jumlah dokumen' },
        { h3: 'Langkah 1: Jenis dokumen' },
        { steps: [
          'Pilih **Organisasi (PAU/Fakultas/Sekolah)**.',
          'Pilih **Jenis dokumen**. Daftar hanya berisi jenis dokumen yang sudah disiapkan Administrator untuk organisasi tersebut.',
          'Periksa ringkasan **Sifat kerahasiaan**, **Alur persetujuan**, dan **Template**. Unduh template bila dokumen perlu disusun mengikuti template tersebut.',
        ] },
        { h3: 'Langkah 2: Dokumen' },
        { steps: [
          'Klik **Pilih file** dan unggah dokumen (**PDF, DOC, atau DOCX**).',
          'Isi **Nomor surat/dokumen**, contoh `SRT/1/00003/2026`.',
        ] },
        { img: 'pengaju-submit-step1.png', caption: 'Form pengajuan satu dokumen' },
        { h3: 'Langkah 3: Pemaraf & penandatangan' },
        { p: 'Susunan pemaraf dan penandatangan diambil otomatis dari pengaturan jenis dokumen. Bila perlu diubah khusus untuk dokumen ini, aktifkan **Ubah susunan penandatangan**, lalu:' },
        { list: [
          'cari nama, jabatan, atau NIK lalu klik **Tambah** untuk menambah pemaraf/penandatangan;',
          'klik ikon **tempat sampah** untuk menghapus;',
          'atur **Tampilkan paraf** / **Tampilkan tanda tangan** untuk menampilkan atau menyembunyikan gambar paraf/tanda tangan.',
        ] },
        alurPersetujuan,
        { h3: 'Langkah 4: Periksa & ajukan' },
        { steps: [
          'Klik **Preview & atur posisi** untuk memeriksa letak paraf/tanda tangan pada dokumen. Posisi mengikuti pengaturan jenis dokumen.',
          'Centang **Saya telah memeriksa data penandatangan dan kesesuaian dokumen di atas**.',
          'Klik **Ajukan**. Sistem membuka **Detail Dokumen** dengan pesan pengajuan berhasil disimpan.',
        ] },
        { img: 'pengaju-submit-preview-ttd.png', caption: 'Preview posisi tanda tangan' },
        { img: 'pengaju-submit-siap-ajukan.png', caption: 'Konfirmasi sebelum mengajukan' },
        { callout: 'tips', text: 'Bila tombol **Ajukan** belum aktif, baca keterangan di sebelahnya, misalnya "Isi nomor surat/dokumen untuk melanjutkan." atau "Centang konfirmasi untuk mengajukan."' },
        { callout: 'penting', text: 'Bila muncul pesan "Masih ada Item Tandatangan yang belum memiliki posisi pada dokumen", berarti jumlah halaman atau ukuran dokumen tidak sesuai template. Sesuaikan dokumen dengan template, atau hubungi Administrator.' },
      ],
    },
    {
      id: 'ajukan-banyak',
      title: 'Mengajukan Banyak Dokumen Sekaligus',
      blocks: [
        { p: 'Gunakan **Banyak dokumen sekaligus** untuk mengajukan banyak dokumen dengan jenis dan penandatangan yang sama.' },
        { steps: [
          'Pilih **Banyak dokumen sekaligus**, lalu pilih **Organisasi** dan **Jenis dokumen**.',
          'Klik **Template CSV** untuk mengunduh contoh file CSV.',
          'Siapkan **file ZIP** berisi seluruh dokumen (.pdf, .doc, atau .docx).',
          'Siapkan **file CSV** dengan format `Nama_File;Nomor_Surat` per baris. Baris pertama adalah judul kolom.',
          'Unggah ZIP pada **File ZIP berisi dokumen** dan CSV pada **File CSV daftar nomor surat**.',
          'Periksa pemaraf & penandatangan, centang konfirmasi, lalu klik **Ajukan**.',
        ] },
        { img: 'pengaju-submit-massal.png', caption: 'Form pengajuan banyak dokumen' },
        { table: {
          head: ['Contoh isi CSV'],
          rows: [['`Nama_File;Nomor_Surat`'], ['`dokumen1.pdf;SRT/001/2026`'], ['`dokumen2.pdf;SRT/002/2026`']],
        } },
        { callout: 'penting', text: 'Nama file di CSV harus sama dengan nama file di dalam ZIP (huruf besar/kecil tidak berpengaruh). Bila ada yang tidak cocok, muncul dialog **Ketidaksesuaian File**. Klik **Kembali dan Perbaiki**, sesuaikan file, lalu ajukan ulang.' },
        { p: 'Setelah diproses, sistem menampilkan ringkasan **Total**, **Berhasil**, dan **Gagal** beserta pesan per dokumen. Klik **Tutup** untuk membuka daftar dokumen.' },
      ],
    },
    {
      id: 'pantau',
      title: 'Memantau Dokumen',
      blocks: [
        { p: 'Menu **Dokumen** menampilkan dokumen yang Anda ajukan. Saring berdasarkan **Nama Penandatangan**, **Nomor Surat**, **Tanggal Pengajuan**, **Jenis Dokumen**, dan **Status Dokumen**, lalu klik **Cari**.' },
        { callout: 'tips', text: 'Filter **Status Dokumen** terisi **Selesai** saat halaman dibuka. Kosongkan filter ini untuk melihat dokumen yang masih diproses.' },
        { img: 'pengaju-dokumen.png', caption: 'Daftar Arsip Dokumen' },
        statusDokumen,
        { h3: 'Detail dokumen' },
        { p: 'Klik **Detail** untuk melihat data pengajuan dan **Riwayat Penandatanganan**: siapa yang sudah memaraf/menandatangani, siapa yang sedang mendapat giliran, dan siapa yang masih menunggu urutan.' },
        { img: 'pengaju-detail-dokumen.png', caption: 'Detail Dokumen dan Riwayat Penandatanganan' },
        { list: [
          '**Preview Dokumen**: melihat dokumen.',
          '**Download**: mengunduh dokumen bertanda tangan (hanya bila status **Selesai**).',
          '**Download ZIP** di daftar dokumen: mengunduh banyak dokumen sekaligus (1–500 dokumen).',
        ] },
      ],
    },
    {
      id: 'batalkan',
      title: 'Membatalkan Pengajuan',
      blocks: [
        { steps: [
          'Buka **Detail** dokumen yang belum berstatus Selesai atau Dibatalkan.',
          'Klik tombol **⋮** (Aksi lainnya) di samping **Preview Dokumen**, lalu pilih **Batalkan pengajuan**.',
          'Konfirmasi pembatalan. Status dokumen menjadi **Dibatalkan**.',
        ] },
        { img: 'pengaju-detail-menu.png', caption: 'Menu Batalkan pengajuan pada Detail Dokumen' },
        { callout: 'penting', text: 'Pembatalan tidak dapat diurungkan. Untuk melanjutkan, buat pengajuan baru dari awal.' },
      ],
    },
    {
      id: 'penolakan',
      title: 'Menindaklanjuti Penolakan',
      blocks: [
        { p: 'Bila penandatangan atau pemaraf menolak, Anda menerima email dan status dokumen menjadi **Dalam Review**. Dokumen tersebut tampil di menu **Penolakan** (Daftar Dokumen yang Ditolak) beserta **Alasan Penolakan**.' },
        { img: 'pengaju-penolakan.png', caption: 'Daftar Dokumen yang Ditolak' },
        { p: 'Buka **Detail** dokumen, lalu pilih salah satu tindakan:' },
        { table: {
          head: ['Tindakan', 'Akibat'],
          rows: [
            ['Konfirm Tolak', 'Menyetujui penolakan. Penandatangan yang menolak **dikeluarkan** dari daftar dan proses berlanjut ke penandatangan berikutnya. Bila tidak ada penandatangan lain, dokumen menjadi **Selesai**; bila ia satu-satunya penandatangan, dokumen menjadi **Dibatalkan**.'],
            ['Sanggah Tolak', 'Menolak alasan penolakan. Isi **catatan** untuk penandatangan; penandatangan dimasukkan kembali, menerima email, dan dokumen kembali **Dalam Proses**.'],
          ],
        } },
        { callout: 'penting', text: '**Konfirm Tolak** tidak tersedia bila dokumen memuat **visual** tanda tangan/paraf penandatangan yang menolak. Dalam kondisi ini, batalkan dokumen lalu buat pengajuan baru dengan susunan penandatangan yang benar.' },
      ],
    },
    {
      id: 'batch-pengaju',
      title: 'Batch',
      blocks: [
        { p: 'Menu **Batch** menampilkan kumpulan dokumen yang dikirim aplikasi eksternal atas nama Anda (mis. SIKERJASAMA). Saring berdasarkan **Nama Penandatangan**, **Nomor Batch**, **Tanggal Pengajuan**, **Jenis Dokumen**, dan **Status Dokumen**.' },
        { img: 'pengaju-batch.png', caption: 'Daftar Arsip Batch' },
        { p: 'Klik **Detail** untuk melihat isi batch. Gunakan **Preview Dokumen** untuk melihat dokumen, **Download** untuk mengunduh ZIP batch yang sudah selesai, atau menu **⋮ › Batalkan pengajuan** untuk membatalkan batch yang belum selesai.' },
      ],
    },
    verifikasi,
    notifikasi,
    gantiPeran,
    akun,
  ],
}

// ---------------------------------------------------------------------------
// PENANDATANGAN
// ---------------------------------------------------------------------------

const penandatangan = {
  kode: 'A03',
  label: 'Penandatangan',
  slug: 'Penandatangan',
  intro: [
    { p: 'Penandatangan (termasuk **pemaraf**) memaraf dan menandatangani dokumen secara elektronik menggunakan sertifikat BSrE:' },
    { list: [
      'melihat daftar permohonan tanda tangan (dokumen satuan dan batch);',
      'memaraf/menandatangani satu atau banyak dokumen sekaligus dengan **passphrase**;',
      'menolak dokumen disertai alasan;',
      'melihat arsip dokumen yang pernah ditandatangani.',
    ] },
    { callout: 'info', text: 'Penandatanganan memakai **sertifikat elektronik BSrE** milik Anda. Siapkan **passphrase** sertifikat BSrE. NIK diambil otomatis dari profil, sehingga tidak perlu diketik.' },
  ],
  sections: [
    masuk('Penandatangan'),
    {
      id: 'permohonan',
      title: 'Daftar Permohonan Tanda Tangan',
      blocks: [
        { p: 'Menu **Permohonan Tandatangan** menampilkan dokumen yang menunggu paraf/tanda tangan Anda. Saring berdasarkan **Username Pengaju**, **Nomor Surat**, **Jenis Dokumen**, dan **Tanggal Pengajuan**, lalu klik **Cari**.' },
        { img: 'ttd-sign.png', caption: 'Daftar Permohonan Tanda Tangan' },
        { table: {
          head: ['Keterangan', 'Arti'],
          rows: [
            ['Sesuai Urutan', 'Giliran Anda. Dokumen dapat dipilih untuk ditandatangani.'],
            ['Menunggu urutan penandatangan', 'Alur **Berjenjang**: penandatangan sebelumnya belum selesai. Belum dapat dipilih.'],
            ['Dalam Proses', 'Penandatanganan dokumen ini sedang diproses sistem.'],
          ],
        } },
      ],
    },
    {
      id: 'ttd-satu',
      title: 'Menandatangani Satu Dokumen',
      blocks: [
        { steps: [
          'Klik **Detail** pada dokumen.',
          'Klik **Preview Dokumen** untuk membaca isi dokumen sebelum menandatangani.',
          'Periksa **Riwayat Penandatanganan** di sebelah kanan untuk memastikan giliran Anda (label **Siap ditandatangan** atau **Posisi Tandatangan saat ini**).',
          'Pada bagian **Tanda tangan dokumen**, isi **Passphrase** sertifikat BSrE Anda. Klik ikon mata untuk menampilkan/menyembunyikan passphrase.',
          'Klik **Tanda Tangan** (atau **Paraf** bila Anda pemaraf).',
        ] },
        { img: 'ttd-detail-sign.png', caption: 'Bagian Tanda tangan dokumen pada Detail Dokumen' },
        { p: 'Bila berhasil, muncul pesan "Dokumen berhasil ditandatangani" dan riwayat penandatanganan diperbarui.' },
        { callout: 'penting', text: 'Passphrase bersifat rahasia. Jangan membagikan passphrase kepada siapa pun, termasuk pengaju atau Administrator.' },
      ],
    },
    {
      id: 'ttd-banyak',
      title: 'Menandatangani Banyak Dokumen Sekaligus',
      blocks: [
        { steps: [
          'Di **Daftar Permohonan Tanda Tangan**, centang dokumen berketerangan **Sesuai Urutan**. Centang di kepala tabel untuk memilih semua.',
          'Di bagian bawah tabel, isi **Input Passphrase**.',
          'Klik **Tanda tangani N dokumen**.',
          'Sistem menampilkan "N Dokumen sedang diproses tandatangan, silakan menunggu notifikasi". Proses berjalan di latar belakang.',
        ] },
        { img: 'ttd-sign-pilih.png', caption: 'Memilih beberapa dokumen dan mengisi passphrase' },
        { p: 'Setelah selesai, muncul pemberitahuan di halaman ini dan pada ikon **lonceng** berisi nomor surat yang berhasil ditandatangani. Klik **x** untuk menutup pemberitahuan.' },
      ],
    },
    {
      id: 'tolak',
      title: 'Menolak Dokumen',
      blocks: [
        { steps: [
          'Buka **Detail** dokumen.',
          'Pada bagian **Tanda tangan dokumen**, klik **Menolak**.',
          'Isi **Alasan Penolakan untuk Pengaju** (wajib).',
          'Klik **Ya** untuk menolak, atau **Batal** untuk kembali.',
        ] },
        { img: 'ttd-tolak-dialog.png', caption: 'Dialog penolakan dokumen' },
        { p: 'Status dokumen menjadi **Dalam Review** dan pengaju menerima email. Pengaju dapat **menyetujui** penolakan (Anda dikeluarkan dari daftar penandatangan) atau **menyanggah** penolakan (dokumen kembali kepada Anda disertai catatan).' },
        { callout: 'info', text: 'Daftar permohonan menampilkan pemberitahuan bila penolakan Anda **disetujui** atau **disanggah** pengaju. Dokumen yang disanggah diberi warna sorotan agar mudah ditemukan.' },
      ],
    },
    {
      id: 'batch-ttd',
      title: 'Menandatangani Batch',
      blocks: [
        { p: 'Menu **Permohonan Tandatangan Batch** menampilkan kumpulan dokumen dari aplikasi eksternal yang menunggu tanda tangan Anda. Satu batch dapat berisi banyak dokumen.' },
        { img: 'ttd-batch-sign.png', caption: 'Daftar Permohonan Tanda Tangan Batch' },
        { steps: [
          'Centang batch yang akan ditandatangani.',
          'Klik **Tanda tangan (N)** di bawah tabel.',
          'Pada dialog **Tanda tangani batch?**, isi **Passphrase sertifikat elektronik**.',
          'Klik **Tanda Tangan**. Proses berjalan di latar belakang; klik **Refresh** untuk memperbarui status.',
        ] },
        { img: 'ttd-batch-sign-dialog.png', caption: 'Dialog tanda tangan batch' },
        { p: 'Klik **Detail** untuk melihat isi batch, **Preview Dokumen**, dan **Histori Tanda Tangan Anda** (riwayat proses tanda tangan beserta pesan kesalahan bila gagal).' },
        { img: 'ttd-detail-batch.png', caption: 'Detail Tandatangan Batch' },
      ],
    },
    {
      id: 'arsip-ttd',
      title: 'Arsip Dokumen',
      blocks: [
        { p: 'Menu **Dokumen** dan **Batch** menampilkan arsip dokumen yang melibatkan Anda sebagai pemaraf/penandatangan. Gunakan filter **Status Dokumen** (awalnya **Selesai**) untuk mencari dokumen dengan status tertentu, lalu klik **Detail** untuk melihat riwayat dan mengunduh dokumen yang sudah selesai.' },
        { img: 'ttd-dokumen.png', caption: 'Arsip dokumen penandatangan' },
        statusDokumen,
      ],
    },
    verifikasi,
    notifikasi,
    gantiPeran,
    akun,
  ],
}

const ROLES = [admin, pengaju, penandatangan]

const FAQ = [
  ['Apakah saya perlu akun untuk memverifikasi dokumen?', 'Tidak. Halaman **Verifikasi File** dapat dibuka tanpa masuk, atau dengan memindai QR code pada dokumen.'],
  ['Mengapa menu yang saya lihat berbeda dengan rekan saya?', 'Menu mengikuti **peran aktif**. Ganti peran melalui avatar di kanan atas bila akun Anda memiliki lebih dari satu peran.'],
  ['Jenis dokumen yang saya cari tidak ada saat pengajuan.', 'Jenis dokumen harus dikonfigurasi Administrator untuk organisasi Anda di **Jenis Dokumen Organisasi**. Hubungi Administrator.'],
  ['Tanda tangan gagal diproses.', 'Pastikan passphrase benar dan **Status BSrE** pada profil Anda aktif. Periksa notifikasi atau **Histori Tanda Tangan** untuk pesan kesalahan, lalu hubungi Administrator bila masalah berlanjut.'],
]

export { DOC, ROLES, FAQ }
export default { DOC, ROLES, FAQ }
